import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, inject, Input, Output } from '@angular/core';
import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { PIconModule } from '@panaya/ui/p-icon';
import { PActionButtonModule } from '@panaya/ui/p-action-button';
import { AiChatUiEventsService } from '../../ai-chat-ui-events.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, merge, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AiChatLayout } from '@panaya/ai-chat/shared';

// TODO: temp — remove once p-picker-single-select is migrated to publishable;
// replace with: PPickerSingleSelectModule + FormsModule + ListItem, bind [ngModel]/ngModelChange
interface LayoutOption {
    id: AiChatLayout;
    name: string;
    icon: string;
}

/** The layout picker's options; Canvas is listed only while a canvas is open. */
export function buildLayoutOptions(translate: (key: string) => string, isCanvasAvailable: boolean): LayoutOption[] {
    const options: LayoutOption[] = [
        { id: 'floating', name: translate('aichat.chat.layout_floating'), icon: 'maximize-2' },
        { id: 'sidebar', name: translate('aichat.chat.layout_sidebar'), icon: 'open-side-panel' }
    ];

    return isCanvasAvailable ? [...options, { id: 'canvas', name: translate('aichat.chat.layout_canvas'), icon: 'layout' }] : options;
}

@Component({
    standalone: true,
    selector: 'p-ai-chat-window-title-bar',
    imports: [
        PIconModule,
        TranslateModule,
        MatTooltipModule,
        NgClass,
        NgFor, // TODO: temp — remove with picker migration
        NgIf, // TODO: temp — remove with picker migration
        PActionButtonModule,
        AsyncPipe
    ],
    templateUrl: './ai-chat-window-title-bar.component.html',
    styleUrls: ['./ai-chat-window-title-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatWindowTitleBarComponent {
    @Input() isDraggable = false;
    @Input() isAssistantWorking = false;
    @Input() currentLayout: AiChatLayout = 'floating';
    /** `true` while an agent has a canvas open; only then does the layout picker list the Canvas option. */
    @Input() set canvasAvailable(value: boolean) {
        this.canvasAvailable$.next(value);
    }
    @Output() layoutChange = new EventEmitter<AiChatLayout>();

    private readonly events = inject(AiChatUiEventsService);
    private readonly translate = inject(TranslateService);
    private readonly canvasAvailable$ = new BehaviorSubject(false);

    // TODO: temp — remove with picker migration
    protected readonly isLayoutMenuOpen$ = new BehaviorSubject(false);

    protected readonly layoutOptions$ = combineLatest([
        merge(of(null), this.translate.onTranslationChange, this.translate.onLangChange),
        this.canvasAvailable$
    ]).pipe(map(([, isCanvasAvailable]) => buildLayoutOptions((key) => this.translate.instant(key), isCanvasAvailable)));

    // TODO: temp — remove with picker migration
    @HostListener('document:click')
    protected onDocumentClick(): void {
        if (this.isLayoutMenuOpen$.getValue()) {
            this.isLayoutMenuOpen$.next(false);
        }
    }

    protected close(): void {
        this.events.closeThreadRequest.emit();
    }

    protected reset(): void {
        this.events.triggerInitThreadRequest();
    }

    // TODO: temp — remove with picker migration
    protected toggleLayoutMenu(event: MouseEvent): void {
        event.stopPropagation();
        this.isLayoutMenuOpen$.next(!this.isLayoutMenuOpen$.getValue());
    }

    protected selectLayout(layout: AiChatLayout): void {
        this.isLayoutMenuOpen$.next(false);
        if (layout !== this.currentLayout) {
            this.layoutChange.emit(layout);
        }
    }
}
